import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";



const PressForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle sending OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/press/forget-password/send-otp`,
        { email: email },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Response sending OTP:", response);

      if (response.status === 200) {
        setOtpSent(true);
        setError('');
        toast.success('OTP sent successfully to your email!');
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (err) {
      console.error("Error sending OTP:", err);
      if (err.response?.status === 404) {
        setError('User not found with this email address');
      } else if (err.response?.status === 500) {
        setError('Failed to send OTP. Please try again later.');
      } else {
        setError('User is not Exist, Please Enter your Email');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/press/forget-password/verify-otp`,
        { email, otp },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Response verifying OTP:", response);
      if (response.status === 200) {
        setShowPasswordModal(true);
        setError('');
      } else {
        setError(response.data.message || 'Invalid OTP');
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Invalid or expired OTP');
      } else {
        setError('Wrong OTP. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  };


  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setResetting(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/press/forget-password/reset-password`,
        { email, newPassword, confirmPassword },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Response resetting password:", response);
      if (response.status === 200) {
        toast.success('Password reset successful! Please login with your new password.');
        setShowPasswordModal(false);
        setTimeout(() => {
          navigate('/press/login');
        }, 1500); // Wait for toast to show
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error("Error resetting password:", err);
      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Password reset failed');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setResetting(false);
    }

  };

  // Resend OTP function
  const handleResendOtp = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/press/forget-password/resend-otp`,
        { email },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Response resending OTP:", response);
      if (response.status === 200) {
        toast.success('OTP resent successfully to your email!');
        setError('');
      } else {
        setError(response.data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      console.error("Error resending OTP:", err);
      if (err.response?.status === 404) {
        setError('User not found with this email address');
      } else if (err.response?.status === 500) {
        setError('Failed to resend OTP. Please try again later.');
      } else {
        setError('Failed to resend OTP. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 mt-16">
      <ToastContainer />
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col lg:flex-row my-8">
        {/* Left Side - Company Branding */}
        <div className="hidden lg:block lg:w-5/12 bg-gradient-to-br from-blue-500 to-purple-600 p-8 relative">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10 h-full flex flex-col justify-center items-center text-white">
            <motion.img
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8 }}
              src="/images/iinsaf.png"
              alt="IINSAF Logo"
              className="w-40 h-auto mb-6 filter brightness-110"
            />
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <h1 className="text-3xl font-bold mb-3">Press Conference Password Recovery</h1>
              <p className="text-lg text-gray-100 mb-6">
                Enter your Email to receive an OTP
              </p>
            </motion.div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="lg:w-7/12 p-8">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-md mx-auto"
          >
            <div className="text-center lg:text-left mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Press Conference Forgot Password</h2>
              <p className="text-gray-600 text-sm mt-1">
                Enter your Email to receive a verification code
              </p>
            </div>

            <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp} className="space-y-6">
              {error && !showPasswordModal && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 text-red-500 p-3 rounded-lg text-sm text-center"
                >
                  {error}
                </motion.div>
              )}

              <div className="relative group">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500"
                  />
                </div>
                <input
                  type="email"
                  required
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter Your Email "
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={otpSent}
                />
              </div>

              {otpSent && (
                <div className="relative group">
                  <input
                    type="text"
                    required
                    className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter OTP Code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                </div>
              )}

              {!otpSent ? (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {isLoading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={verifying}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    {verifying ? 'Verifying...' : 'Verify OTP'}
                  </button>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={handleResendOtp}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Resend OTP
                  </button>
                </div>
              )}

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/press/login')}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Back to Login
                </button>
              </div>
            </form>
          </motion.div>

          {/* Password Reset Modal */}
          {showPasswordModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-all">
              <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm relative animate-fadeIn">
                <div className="flex flex-col items-center mb-4">
                  <svg className="w-12 h-12 text-green-500 mb-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <h3 className="text-2xl font-bold mb-1 text-center text-gray-800">OTP Verified!</h3>
                  <p className="text-gray-500 text-center text-sm mb-2">Now set your new password below.</p>
                </div>
                {error && (
                  <div className="bg-red-50 text-red-500 p-2 rounded-lg text-sm text-center mb-2">{error}</div>
                )}
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <input
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      required
                      autoFocus
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                      onClick={() => setShowNewPassword((prev) => !prev)}
                    >
                      <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={resetting}
                    className="w-full py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {resetting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
                <button
                  type="button"
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl"
                  onClick={() => setShowPasswordModal(false)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PressForgotPassword;